import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import { Box, Paper, Typography, TextField, Button, Stack, Alert } from '@mui/material'

interface Props {
  values?: { email?: string; name?: string }
  errors?: Record<string, string[]>
}

interface SharedProps {
  flash?: { notice?: string | null; alert?: string | null }
}

function SignUp({ values = {}, errors: serverErrors = {} }: Props) {
  const page = usePage<SharedProps>()
  const flash = page.props.flash || {}

  const { data, setData, post, processing } = useForm({
    user: {
      name: values.name || '',
      email: values.email || '',
      password: '',
      password_confirmation: '',
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/users')
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 120px)', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 460, width: '100%' }}>
        <Typography variant="h4" sx={{ mb: 1, textAlign: 'center' }}>Create your account</Typography>
        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          Save places, follow groups, and contribute to the map.
        </Typography>

        {flash.alert && <Alert severity="error" sx={{ mb: 2 }}>{flash.alert}</Alert>}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={data.user.name}
              onChange={(e) => setData('user', { ...data.user, name: e.target.value })}
              error={Boolean(serverErrors.name)}
              helperText={serverErrors.name?.join(', ')}
              required
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={data.user.email}
              onChange={(e) => setData('user', { ...data.user, email: e.target.value })}
              error={Boolean(serverErrors.email)}
              helperText={serverErrors.email?.join(', ')}
              required
              autoComplete="email"
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={data.user.password}
              onChange={(e) => setData('user', { ...data.user, password: e.target.value })}
              error={Boolean(serverErrors.password)}
              helperText={serverErrors.password?.join(', ') || 'At least 6 characters'}
              required
              autoComplete="new-password"
              fullWidth
            />
            <TextField
              label="Confirm password"
              type="password"
              value={data.user.password_confirmation}
              onChange={(e) => setData('user', { ...data.user, password_confirmation: e.target.value })}
              error={Boolean(serverErrors.password_confirmation)}
              helperText={serverErrors.password_confirmation?.join(', ')}
              required
              autoComplete="new-password"
              fullWidth
            />
            <Button type="submit" variant="contained" size="large" disabled={processing}>
              Sign up
            </Button>
          </Stack>
        </form>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link href="/users/sign_in">Already have an account? Sign in</Link>
        </Box>
      </Paper>
    </Box>
  )
}

;(SignUp as React.ComponentType & { noLayout?: boolean }).noLayout = true

export default SignUp
